import { ApolloProvider } from "@apollo/client/react";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { GlobalStyles } from "@mui/material";
import { globalStyles } from "@src/App.styles";
import {
    type AttributeConfig,
    AttributeConfigProvider,
    defaultAttributeConfig,
    type InputAttributeConfig,
} from "@src/context/attributeConfig/AttributeConfigContext";
import { GlobalStateProvider } from "@src/context/globalState/GlobalStateContext";
import { RouterProvider } from "@src/context/router/RouterContext";
import { getInitialRoute } from "@src/context/router/RouterContext.utils";
import { Main } from "@src/main/Main";
import { ThemeProvider } from "@src/theme/ThemeProvider";
import { apolloClient } from "@src/utils/apollo/apolloClient";
import { LocalizationProvider } from "@src/utils/localization/LocalizationProvider";
import { TrackingProvider } from "@src/utils/tracking/TrackingProvider";
import { SnackbarProvider } from "notistack";
import { type FunctionComponent } from "react";

interface AppProps {
    inputAttributeConfig: InputAttributeConfig;
    shadowRoot?: ShadowRoot;
    shadowRootElement?: HTMLElement;
}

export const App: FunctionComponent<AppProps> = ({ shadowRoot, shadowRootElement, inputAttributeConfig }) => {
    const emotionCache = createCache({
        key: "css",
        prepend: true,
        container: shadowRoot,
    });

    const attributeConfig: AttributeConfig = { ...defaultAttributeConfig, ...inputAttributeConfig };

    return (
        <CacheProvider value={emotionCache}>
            <GlobalStyles styles={globalStyles} />

            <ThemeProvider inputAttributeConfig={inputAttributeConfig} shadowRootElement={shadowRootElement}>
                <ApolloProvider client={apolloClient}>
                    <LocalizationProvider language={attributeConfig.locale}>
                        <AttributeConfigProvider config={attributeConfig}>
                            <TrackingProvider>
                                <RouterProvider initialRoute={getInitialRoute(attributeConfig)}>
                                    <GlobalStateProvider
                                        initialModelArticleNumber={attributeConfig.entryPoint?.modelArticleNumber}
                                        attributeBrands={attributeConfig.brands}
                                    >
                                        <SnackbarProvider domRoot={shadowRootElement}>
                                            <Main />
                                        </SnackbarProvider>
                                    </GlobalStateProvider>
                                </RouterProvider>
                            </TrackingProvider>
                        </AttributeConfigProvider>
                    </LocalizationProvider>
                </ApolloProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};
